import React from 'react'
import { Button, Card, CardHeader, CardContent, Input, Label, Table, Tr, Th, Td } from '@/shared/ui'
import BatchAddBybit from './BatchAddBybit'

export type Option = { symbol: string; base: string; quote: string; name?: string }

type Props = {
  open: boolean
  items: Option[]
  filter: string
  onChangeFilter: (v: string) => void
  sort: 'symbol-asc' | 'symbol-desc' | 'name-asc' | 'name-desc'
  onChangeSort: (v: 'symbol-asc' | 'symbol-desc' | 'name-asc' | 'name-desc') => void
  onAddOne: (o: Option) => Promise<void>
  adding?: string | null
  batchMode: boolean
  onToggleBatchMode: (v: boolean) => void
  existingSymbols: Set<string>
  className?: string
}

export default function AddBybitPanel({
  open,
  items,
  filter,
  onChangeFilter,
  sort,
  onChangeSort,
  onAddOne,
  adding,
  batchMode,
  onToggleBatchMode,
  existingSymbols,
  className,
}: Props) {
  const [limit, setLimit] = React.useState(120)
  const scrollRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!open) setLimit(120)
  }, [open])

  React.useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40
      if (nearBottom) {
        ticking = true
        setLimit((l) => l + 120)
        setTimeout(() => {
          ticking = false
        }, 300)
      }
    }
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [open])

  const visible = React.useMemo(() => items.slice(0, limit), [items, limit])

  if (!open) return null

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="text-base font-medium">Adicionar (Bybit USDT)</div>
          <div className="text-xs text-foreground/60">Fonte: símbolos Bybit com paridade USDT (mercado padrão)</div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Buscar por nome ou símbolo (ex.: SOL, SOLUSDT)"
              value={filter}
              onChange={(e) => onChangeFilter(e.target.value)}
              className="h-8 sm:h-9 text-sm flex-1 min-w-0"
            />
            <select
              className="h-8 sm:h-9 w-[160px] rounded-md border border-border bg-background px-2 text-sm"
              value={sort}
              onChange={(e) => onChangeSort(e.target.value as any)}
            >
              <option value="symbol-asc">Símbolo (A-Z)</option>
              <option value="symbol-desc">Símbolo (Z-A)</option>
              <option value="name-asc">Nome (A-Z)</option>
              <option value="name-desc">Nome (Z-A)</option>
            </select>
          </div>

          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <input
                id="batch-mode"
                type="checkbox"
                className="h-4 w-4"
                checked={batchMode}
                onChange={(e) => onToggleBatchMode(e.currentTarget.checked)}
              />
              <Label htmlFor="batch-mode" className="text-xs">
                Modo seleção em lote
              </Label>
            </div>
            <div className="text-foreground/60">Paridade fixa: USDT</div>
          </div>

          {batchMode ? (
            <BatchAddBybit
              items={visible.map(
                (o) =>
                  ({
                    symbol: o.symbol,
                    baseCoin: o.base,
                    quoteCoin: o.quote,
                    name: o.name,
                  }) as any,
              )}
              existing={existingSymbols}
            />
          ) : (
            <div className="rounded-md border border-border/50 p-2">
              <div ref={scrollRef} className="max-h-72 overflow-auto">
                <Table>
                  <thead>
                    <Tr>
                      <Th>Nome / Símbolo</Th>
                      <Th>Paridade</Th>
                      <Th>Ação</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {visible.map((o) => (
                      <Tr key={o.symbol}>
                        <Td>
                          <div className="text-sm font-medium">{o.name || o.symbol.replace(/USDT$/, '')}</div>
                          <div className="text-xs text-foreground/60">{o.symbol}</div>
                        </Td>
                        <Td className="text-sm">
                          {o.base}/{o.quote}
                        </Td>
                        <Td>
                          <Button
                            className="h-8 px-3 py-1 text-xs"
                            disabled={!!adding || existingSymbols.has(o.symbol)}
                            onClick={() => onAddOne(o)}
                          >
                            {existingSymbols.has(o.symbol)
                              ? 'Já cadastrado'
                              : adding === o.symbol
                                ? 'Adicionando…'
                                : 'Adicionar'}
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

